// src/pages/Reports.js
import React, { useState } from 'react'
import {
  Box,
  Typography,
  Button,
  TextField,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  TablePagination,
  CircularProgress,
} from '@mui/material'
import dayjs from 'dayjs'
import * as XLSX from 'xlsx'
import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'

// Backend base URL (same pattern as other pages)
const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || 'http://157.180.43.56:5050'

// Safe number helper
const num = (v) => {
  const n = Number(v)
  return Number.isFinite(n) ? n : 0
}

export default function Reports() {
  const [payments, setPayments] = useState([])
  const [fromDate, setFromDate] = useState('')
  const [toDate, setToDate] = useState(dayjs().format('YYYY-MM-DD'))
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [errorMsg, setErrorMsg] = useState('')
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)

  const handleChangePage = (_event, newPage) => setPage(newPage)

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  const fetchReport = async () => {
    if (!fromDate || !toDate) {
      alert('Please select both dates')
      return
    }

    setLoading(true)
    setErrorMsg('')

    try {
      const url = new URL('/api/reports/general', API_BASE_URL)
      url.searchParams.set('startDate', fromDate)
      url.searchParams.set('endDate', toDate)

      const res = await fetch(url.toString())
      if (!res.ok) {
        throw new Error(`General report request failed with status ${res.status}`)
      }

      const data = await res.json()
      const rows = data.records || []
      setPayments(rows)
      setTotal(num(data.totalCollected || 0))
      setPage(0)
    } catch (err) {
      console.error('Error fetching general report:', err)
      setPayments([])
      setTotal(0)
      setErrorMsg('Failed to load General Report from backend.')
    } finally {
      setLoading(false)
    }
  }

  const exportToExcel = () => {
    if (payments.length === 0) return

    const sheetData = payments.map((p) => ({
      Date: p.payment_date,
      Vehicle: p.plate_number || 'N/A',
      Driver: p.driver_name || '',
      Amount: num(p.amount),
      Status: p.status || '',
    }))

    const ws = XLSX.utils.json_to_sheet(sheetData)
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'GeneralReport')
    XLSX.writeFile(wb, 'General_Report.xlsx')
  }

  const exportToPDF = () => {
    if (payments.length === 0) return

    const doc = new jsPDF()
    doc.setFontSize(14)
    doc.text('General Report', 14, 16)
    doc.setFontSize(10)
    doc.text(
      `Range: ${fromDate || 'N/A'} — ${toDate || 'N/A'}`,
      14,
      24
    )

    autoTable(doc, {
      startY: 32,
      head: [['Date', 'Vehicle', 'Driver', 'Amount (TZS)', 'Status']],
      body: payments.map((p) => [
        p.payment_date,
        p.plate_number || 'N/A',
        p.driver_name || '',
        num(p.amount).toLocaleString(),
        p.status || '',
      ]),
    })
    doc.save('General_Report.pdf')
  }

  const paginatedData = payments.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  )

  return (
    <Box sx={{ padding: 3 }}>
      <Typography variant="h5" gutterBottom>
        General Report (All Vehicles)
      </Typography>

      {errorMsg && (
        <Typography sx={{ mb: 2 }} color="warning.main">
          {errorMsg}
        </Typography>
      )}

      <Box
        sx={{
          display: 'flex',
          gap: 2,
          mb: 3,
          flexWrap: 'wrap',
          alignItems: 'center',
        }}
      >
        <TextField
          type="date"
          label="From"
          InputLabelProps={{ shrink: true }}
          value={fromDate}
          onChange={(e) => setFromDate(e.target.value)}
          size="small"
        />
        <TextField
          type="date"
          label="To"
          InputLabelProps={{ shrink: true }}
          value={toDate}
          onChange={(e) => setToDate(e.target.value)}
          size="small"
        />
        <Button variant="contained" onClick={fetchReport} disabled={loading}>
          {loading ? 'Loading…' : 'Generate'}
        </Button>
        {payments.length > 0 && (
          <>
            <Button
              onClick={exportToExcel}
              variant="outlined"
              disabled={payments.length === 0}
            >
              Export Excel
            </Button>
            <Button
              onClick={exportToPDF}
              variant="outlined"
              disabled={payments.length === 0}
            >
              Export PDF
            </Button>
          </>
        )}
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 5 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Paper sx={{ p: 2, backgroundColor: '#e3f2fd', mb: 2 }}>
            <Typography variant="h6">
              Total Collected: {total.toLocaleString()} TZS
            </Typography>
          </Paper>

          {payments.length === 0 ? (
            <Typography variant="body1" color="text.secondary">
              No payments found for the selected date range.
            </Typography>
          ) : (
            <Paper sx={{ mt: 2, overflowX: 'auto' }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <strong>Date</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Vehicle</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Driver</strong>
                    </TableCell>
                    <TableCell align="right">
                      <strong>Amount (TZS)</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Status</strong>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedData.map((p) => (
                    <TableRow key={p.id}>
                      <TableCell>
                        {dayjs(p.payment_date).format('YYYY-MM-DD')}
                      </TableCell>
                      <TableCell>{p.plate_number || 'N/A'}</TableCell>
                      <TableCell>{p.driver_name || ''}</TableCell>
                      <TableCell align="right">
                        {num(p.amount).toLocaleString()}
                      </TableCell>
                      <TableCell>{p.status || ''}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <TablePagination
                rowsPerPageOptions={[10, 25, 50, 100]}
                component="div"
                count={payments.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </Paper>
          )}
        </>
      )}
    </Box>
  )
}